import React, { useEffect, useState } from "react";
import { Grid, Popover, Stack, TextField, Typography } from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import globalStyles from "../../../../../assets/styles/global.module.scss";
import styles from "../../../../../assets/styles/componentStyles/templateLibraryStyles/whatsappTemplatePreview.module.scss";
import formStyles from "../../../../../assets/styles/componentStyles/templateLibraryStyles/modifyWhatsAppTemplate/whatsAppTemplateForm.module.scss";

import GlobalButton from "../../../../utility/globalButton/button";
import IconsColor from "../../../../utility/iconsColor";
import { useTemplateLibraryStore } from "../../../../../store/templateLibraryStore";
import { useCarouselFunctions } from "../../utils/useCarouselFunctions";
import { dynamicUrlVisitWebsiteBtnTooltipText } from "../../../channels/global/whatsapp/templates/utils";
import { formatString, toCapitalizedCase } from "../../../../../utils/constants";
import { commonButtonStyle, getSubButtonText, hasDuplicateButtonText, isFlowButton, isInvalidDynamicWebsiteUrl, isInvalidUrl, isTemplateCreated, subButtonTypeIconMap } from "../../constant";
import { useShallow } from "zustand/react/shallow";
import { errorColor, primaryColor } from "../../../../../utils/colors";
import TouchTooltip from "../../utils/TouchTooltip";
import VariableDisclaimer from "../../utils/VariableDisclaimer";

const CarouselTemplateCardButtonsPreview = (props: any) => {

    const { cardData, cardIndex, editable = false, getInput, flagShowVariableTooltipForVisitWebsite, checkForAddDynamicVar,
        carouselInvalidId, getWebsiteUrlValue, inValidIdRefCarousel, setCarouselInvalidId, handleSampleTextChangeCarousel
    } = props;

    const { createTemplatePayload, whatsappTemplateButtonGenericData, invalidPayloadObjectId } = useTemplateLibraryStore(useShallow((state) => ({
        createTemplatePayload: state.createTemplatePayload,
        whatsappTemplateButtonGenericData: state.whatsappTemplateButtonGenericData,
        invalidPayloadObjectId: state.invalidPayloadObjectId,
    })));

    const { isScreenBelow1600, handleDeleteActionBtnForCarousel } = useCarouselFunctions();

    const [openButtonIndex, setOpenButtonIndex] = useState<number | null>(null);
    const [buttonAnchorEl, setButtonAnchorEl] = useState<HTMLElement | null>(null);

    // Constants
    const sortedButtons = [...(cardData?.whatsapp_template_button ?? [])].sort(
        (a, b) => Number(a?.whatsapp_template_button_type_id) - Number(b?.whatsapp_template_button_type_id)
    );

    const openButton = (openButtonIndex !== null) ? cardData?.whatsapp_template_button?.[openButtonIndex] : null;

    // Functions
    const getWhatsappTemplateButtonIndex = (action: any) =>
        cardData?.whatsapp_template_button?.findIndex((btn: any) => btn === action) ?? -1;

    const handlePopoverClose = () => {
        setButtonAnchorEl(null);
        setOpenButtonIndex(null);
    };

    const handleButtonClick = (event: React.MouseEvent<HTMLElement>, action: any) => {
        event.stopPropagation();
        if (editable) {
            const whatsappTemplateButtonIndex = getWhatsappTemplateButtonIndex(action);
            if (whatsappTemplateButtonIndex >= 0) {
                setButtonAnchorEl(event.currentTarget);
                setOpenButtonIndex(whatsappTemplateButtonIndex);
            }
        }
    };

    const isButtonInvalid = (whatsappTemplateButtonIndex: number): boolean => {
        if (!editable || !cardData?.whatsapp_template_button || isTemplateCreated(cardData)) {
            return false;
        }

        // Check if the button data is empty
        const action = cardData?.whatsapp_template_button?.[whatsappTemplateButtonIndex];
        if (!action?.whatsapp_template_button_data?.length) {
            return false;
        }

        // Check if the button text is unique
        const templateButtons = cardData?.whatsapp_template_button ?? [];
        if ((action?.whatsapp_template_button_data ?? []).some((_: any, dataIndex: number) =>
            hasDuplicateButtonText(templateButtons, whatsappTemplateButtonIndex, dataIndex)
        )) {
            return true;
        }

        for (const buttonData of action?.whatsapp_template_button_data) {
            const fieldDataList = buttonData?.whatsapp_template_button_sub_type_field_data ?? [];

            for (const field of fieldDataList) {
                if (field?.field_static_value_flag === true) continue;

                const fieldName = (field?.field_name ?? "").toLowerCase();
                const fieldType = (field?.field_type ?? "").toLowerCase();
                const value = field?.whatsapp_template_button_sub_type_field_value;

                if (fieldName === "website url") {
                    const urlTypeField = fieldDataList.find((f: any) => (f?.field_name ?? "").toLowerCase() === "url type") as any;
                    const selectedUrlType = urlTypeField?.field_option_data?.find(
                        (option: any) => option?.field_option_id === urlTypeField?.whatsapp_template_button_sub_type_field_option_id
                    );
                    const isDynamicUrl = selectedUrlType?.field_option_value?.toLowerCase() === "dynamic";

                    if (isDynamicUrl) {
                        const sample = field?.whatsapp_template_button_field_data_variable?.[0]?.whatsapp_template_dynamic_variable_sample_value?.trim();
                        if (!sample || isInvalidUrl(sample)) return true;
                        
                        const strVal = typeof value === "string" ? value.trim() : "";
                        if (!strVal || isInvalidDynamicWebsiteUrl(strVal)) return true;
                    } else {
                        const strVal = typeof value === "string" ? value.trim() : "";
                        if (!strVal || isInvalidUrl(strVal)) return true;
                    }
                    continue;
                }

                switch (true) {
                    case fieldType === "textfield" && fieldName !== "button description" && fieldName !== "website url": {
                        const strVal = typeof value === "string" ? value.trim() : "";
                        if (!strVal) return true;
                        break;
                    }

                    case fieldType === "autocomplete": {
                        const optionId = field?.whatsapp_template_button_sub_type_field_option_id;
                        if (optionId == null || Number(optionId) === 0) {
                            if (!isFlowButton(action?.whatsapp_template_button_type_id, whatsappTemplateButtonGenericData) || value == null) {
                                return true;
                            }
                        }
                        break;
                    }

                    case fieldName === "payload" && Array.isArray(value): {
                        const hasEmpty = value?.some((payload: any) => (!payload?.payload_key?.trim() || !payload?.payload_key_value?.trim()));
                        if (hasEmpty) return true;
                        break;
                    }

                    default: {
                        const fieldId = field?.whatsapp_template_button_sub_type_field_id;
                        if (invalidPayloadObjectId?.length && fieldId != null && invalidPayloadObjectId.includes(fieldId)) {
                            return true;
                        }
                    }
                }
            }
        }

        return false;
    };

    const getButtonPreview = (action: any) => {
        const subBtnData = action?.whatsapp_template_button_data?.[0];
        const buttonText = getSubButtonText(subBtnData)?.trim() ?? "";
        
        const subTypeName = subBtnData?.whatsapp_template_button_sub_type_name?.toLowerCase();
        const icon = subTypeName ? subButtonTypeIconMap[subTypeName as keyof typeof subButtonTypeIconMap] : undefined;

        return { buttonText, icon };
    };

    useEffect(() => {
        if (!editable) {
            handlePopoverClose();
        }
    }, [editable]);

    if (!sortedButtons.length) {
        return null;
    }

    return (
        <>
            <Stack>
                {sortedButtons?.map((action: any) => {
                    const whatsappTemplateButtonIndex = getWhatsappTemplateButtonIndex(action);
                    const { buttonText, icon } = getButtonPreview(action);
                    
                    return (
                        <GlobalButton
                            key={whatsappTemplateButtonIndex}
                            className=""
                            sx={{
                                cursor: editable ? "pointer" : "default",
                                ...commonButtonStyle,
                                maxWidth: "100%",
                                height: "36px",
                                minHeight: "36px",
                                maxHeight: "36px",
                                py: "6px !important",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                ...(editable && isButtonInvalid(whatsappTemplateButtonIndex) && {
                                    border: `1px solid ${errorColor} !important`,
                                    boxSizing: "border-box",
                                }),
                            }}
                            startIcon={icon}
                            buttonText={buttonText}
                            onClick={(e) => handleButtonClick(e, action)}
                        />
                    );
                })}
            </Stack>


            {editable && openButtonIndex !== null && buttonAnchorEl && openButton && (
                <Popover
                    open={openButtonIndex !== null}
                    anchorEl={buttonAnchorEl}
                    onClose={handlePopoverClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    PaperProps={{
                        className: styles.addButtonDialogPaper
                    }}
                >
                    <Grid container className={styles.buttonPreviewPopover__callToActionGrid}>
                        
                        <Grid item xs={12} display="flex" alignItems="start" justifyContent="space-between" flexWrap="nowrap" gap={0.5}
                            className={styles.buttonPreviewPopover__stickyHeader}
                        >
                            <Grid item className={styles.buttonPreviewPopover__callToActionGrid__text} sx={{ flex: 1, minWidth: 0 }}>
                                {toCapitalizedCase(formatString(
                                    whatsappTemplateButtonGenericData?.find(
                                        (btn: any) => Number(btn?.whatsapp_template_button_type_id) === Number(openButton?.whatsapp_template_button_type_id)
                                    )?.whatsapp_template_button_type_name
                                )) ?? ""}
                            </Grid>

                            {editable && !isTemplateCreated(createTemplatePayload) && (
                                <DeleteIcon
                                    className={`${globalStyles.displayFlex} ${styles.buttonPreviewPopover__closeActionButton}`}
                                    onClick={() => handleDeleteActionBtnForCarousel(openButton?.whatsapp_template_button_type_id, openButton?.whatsapp_template_button_data?.[0]?.whatsapp_template_button_sub_type_id, 0, openButtonIndex, inValidIdRefCarousel, carouselInvalidId, setCarouselInvalidId)}
                                    sx={{ fontSize: "10px", cursor: "pointer" }}
                                />
                            )}

                            <CloseIcon
                                className={`${globalStyles.displayFlex} ${styles.buttonPreviewPopover__closeActionButton}`}
                                onClick={handlePopoverClose}
                                sx={{ fontSize: "10px", cursor: "pointer" }}
                            />
                        </Grid>

                        <Grid item xs={12} className={styles.buttonPreviewPopover__callToActionGrid__content}>
                            
                            {openButton?.whatsapp_template_button_data?.map((field: any, whatsappTemplateButtonDataIndex: number) => (
                                
                                <React.Fragment key={whatsappTemplateButtonDataIndex}>
                                    <Grid container direction={{ xs: "column" }} gap="15px" alignItems={{ xs: "start" }} justifyContent="start" flexWrap={!isScreenBelow1600 ? "nowrap" : "wrap"} 
                                        className={styles.mobileViewActions}
                                    >
                                        {field?.whatsapp_template_button_sub_type_field_data
                                            ?.filter((subType: any) => subType?.field_name?.toLowerCase() !== "payload")
                                            ?.sort((a: any, b: any) => a?.whatsapp_template_button_sub_type_field_id - b?.whatsapp_template_button_sub_type_field_id)
                                            ?.map((subField: any, subFieldIndex: number) => (
                                                
                                                <React.Fragment key={subFieldIndex}>
                                                    <Grid item xs={12} sx={{ width: "100%" }}>
                                                        {getInput(
                                                            openButtonIndex,
                                                            subField?.field_type,
                                                            subField,
                                                            openButton?.whatsapp_template_button_type_id,
                                                            whatsappTemplateButtonDataIndex,
                                                            subFieldIndex,
                                                            field?.whatsapp_template_button_sub_type_id,
                                                            field?.whatsapp_template_button_sub_type_field_data,
                                                            true,
                                                            cardIndex,
                                                            true
                                                        )}
                                                    </Grid>

                                                    {flagShowVariableTooltipForVisitWebsite(subField, field?.whatsapp_template_button_sub_type_field_data) && (
                                                        <Grid item xs={12} className={styles.buttonPreviewPopover__visitWebsiteVariableText}>
                                                            <span className={styles.visitWebsiteVariableKeyText}>
                                                                {`{{1}}`}
                                                            </span>
                                                            
                                                            <TouchTooltip title={dynamicUrlVisitWebsiteBtnTooltipText()} >
                                                                <span className={`${globalStyles.clickable} ${styles.visitWebsiteVariableInfoIcon}`}>
                                                                    <IconsColor fillColor={primaryColor} iconName="info" height="13" width="13" />
                                                                </span>
                                                            </TouchTooltip>
                                                        </Grid>
                                                    )}
                                                </React.Fragment>
                                            ))
                                        }

                                    </Grid>

                                    {checkForAddDynamicVar(openButton, field) && (
                                        <Grid container>
                                            <Grid item xs={12} sx={{ margin: "6px 0px 8px 0px", paddingBottom: "5px" }}>
                                                <Grid container direction={"row"} alignItems={"center"} gap={0.5} flexWrap="nowrap"
                                                    className={formStyles.whatsappTemplateFormButtons__sampleVariableRow}
                                                >
                                                    <Grid item xs={3.5}>
                                                        <Typography
                                                            component="span"
                                                            className={`${formStyles.sampleVariableKeyText} ${formStyles.whatsappTemplateFormButtons__sampleVariableKey}`}
                                                        >
                                                            {"{{1}}"}
                                                        </Typography>
                                                    </Grid>

                                                    <Grid item xs={8.5} key={field?.whatsapp_template_button_sub_type_id}>
                                                        <TextField
                                                            size="small"
                                                            variant="outlined"
                                                            className={styles.buttonPreviewPopover__templateGlobalTextfield}
                                                            disabled={isTemplateCreated(createTemplatePayload)}
                                                            onChange={(e) => handleSampleTextChangeCarousel(openButtonIndex, whatsappTemplateButtonDataIndex, e.target.value, true, cardIndex)}
                                                            fullWidth
                                                            autoComplete="off"
                                                            inputProps={{ dir: 'auto' }}
                                                            sx={{
                                                                "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                                    border: carouselInvalidId?.find((ids: any) => ids?.index === cardIndex)?.inValidIds?.includes(String(whatsappTemplateButtonDataIndex + openButtonIndex))
                                                                        ? "1px solid red"
                                                                        : "#D7D7D7",
                                                                    borderRadius: "10px",
                                                                },
                                                            }}
                                                            placeholder={`Enter full URL for ${getWebsiteUrlValue(field?.whatsapp_template_button_sub_type_field_data)}`}
                                                            value={
                                                                cardData?.whatsapp_template_button.find((btn: any, ind: number) => ind === openButtonIndex)
                                                                    ?.whatsapp_template_button_data.find((btnField: any, ind: number) => ind === whatsappTemplateButtonDataIndex)
                                                                    ?.whatsapp_template_button_sub_type_field_data.find((subType: any) => subType?.field_name?.toLowerCase() === "website url")
                                                                    ?.whatsapp_template_button_field_data_variable?.[0]?.whatsapp_template_dynamic_variable_sample_value || ""
                                                            }
                                                        />

                                                        {carouselInvalidId?.find((ids: any) => ids?.index === cardIndex)?.inValidIds?.includes(String(whatsappTemplateButtonDataIndex + openButtonIndex)) && (
                                                            <span className={formStyles.errorTextWithTopMargin}>
                                                                Please Enter Valid URL
                                                            </span>
                                                        )}
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            {(cardData?.whatsapp_template_button.find((btn: any, ind: number) => ind === openButtonIndex)
                                                ?.whatsapp_template_button_data.find((btnField: any, ind: number) => ind === whatsappTemplateButtonDataIndex)
                                                ?.whatsapp_template_button_sub_type_field_data.find((subType: any) => subType?.field_name?.toLowerCase() === "website url")
                                                ?.whatsapp_template_button_field_data_variable?.[0]?.whatsapp_template_dynamic_variable_sample_value || "").length === 0 && (
                                                <Grid item xs={12} sx={{ paddingBottom: "12px" }}>
                                                    <VariableDisclaimer text="Add sample URL" />
                                                </Grid>
                                            )}
                                        </Grid>
                                    )}
                                </React.Fragment>
                            ))}

                        </Grid>
                    </Grid>
                </Popover>
            )}
        </>
    );
};

export default CarouselTemplateCardButtonsPreview;